import axios, {
    AxiosHeaders,
    AxiosRequestConfig,
    RawAxiosRequestHeaders,
} from 'axios'
import { ResStatus, ResWrapper } from './ResWrapper'
import { AuthHelper } from '../auth/AuthHelper'

class HttpWrapper {
    private static instance: HttpWrapper

    public constructor() {
        if (HttpWrapper.instance) {
            return HttpWrapper.instance
        }

        HttpWrapper.instance = this
    }

    public async get<TRes>(
        relativeUrl: string,
        queryParams?: URLSearchParams,
        config?: AxiosRequestConfig,
        authorised: boolean = true,
    ): Promise<TRes> {
        const fullUrl = new URL(
            relativeUrl,
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        queryParams = this.appendTenantOverrideId(queryParams)

        if (queryParams) {
            fullUrl.search = queryParams.toString()
        }

        const authConfig: AxiosRequestConfig = {
            ...config,
            withCredentials: true,
            headers: authorised
                ? this.appendAuthHeaders(config?.headers)
                : this.normalizeHeaders(config?.headers),
        }

        const response = await axios.get(fullUrl.toString(), authConfig)

        // Bypass JSON processing if responseType is blob
        if (config?.responseType === 'blob') {
            return response.data as TRes
        }

        return this.handleRes<TRes>(response.data)
    }

    public async post<TReq, TRes>(
        relativeUrl: string,
        data?: TReq,
        queryParams?: URLSearchParams,
        config?: AxiosRequestConfig,
        authorised: boolean = true,
    ): Promise<TRes> {
        const fullUrl = new URL(
            relativeUrl,
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        // if (queryParams) {
        //     queryParams.append('apiKey', this.getApiKey())
        // } else {
        //     queryParams = new URLSearchParams({ apiKey: this.getApiKey() })
        // }

        queryParams = this.appendTenantOverrideId(queryParams)

        if (queryParams) {
            fullUrl.search = queryParams.toString()
        }

        const response = await axios.post(fullUrl.toString(), data, {
            ...config,
            withCredentials: true,
            headers: authorised
                ? this.appendAuthHeaders(config?.headers)
                : this.normalizeHeaders(config?.headers),
        })

        return this.handleRes<TRes>(response.data)
    }

    private appendTenantOverrideId(
        queryParams?: URLSearchParams,
    ): URLSearchParams | undefined {
        const tenantOverrideId = AuthHelper.getTenantOverrideId()

        if (tenantOverrideId) {
            if (queryParams) {
                queryParams.append('tenantOverrideId', tenantOverrideId)
            } else {
                queryParams = new URLSearchParams({ tenantOverrideId })
            }
        }

        return queryParams
    }

    private appendAuthHeaders(
        existingHeaders?: RawAxiosRequestHeaders | AxiosHeaders,
    ): Record<string, string> {
        // Normalize existing headers
        const headers: Record<string, string> = {}

        // If existingHeaders is provided, convert its values to strings
        if (existingHeaders && typeof existingHeaders === 'object') {
            Object.entries(existingHeaders).forEach(([key, value]) => {
                if (value !== undefined) {
                    headers[key] = String(value)
                }
            })
        }

        return headers
    }

    private normalizeHeaders(
        existingHeaders?: RawAxiosRequestHeaders | AxiosHeaders,
    ): Record<string, string> | undefined {
        if (!existingHeaders) {
            return undefined
        }

        const headers: Record<string, string> = {}
        Object.entries(existingHeaders).forEach(([key, value]) => {
            if (value !== undefined) {
                headers[key] = String(value)
            }
        })

        return headers
    }

    private handleRes<TRes>(resData: ResWrapper<TRes>): TRes {
        // if we're in here that means we got a 2xx response - but did the server respond with an error?

        if (resData.status === ResStatus.ERROR) {
            // generic error - we can't handle this (as opposed to a token refresh required etc)
            const formattedMsg = this.formatErrorMsg(resData.errorMsg)
            throw new Error(
                `There was an issue communicating with the Contxt server: ${formattedMsg}`,
            )
        } else {
            return resData.data!
        }
    }

    private formatErrorMsg(errorMsg: unknown): string {
        // Only rely on the backend-provided errorMsg.
        // Keep this generic to avoid coupling to backend error structures.
        if (typeof errorMsg === 'string' && errorMsg.trim().length > 0) {
            return errorMsg
        }
        if (errorMsg == null) return 'Unknown error'
        try {
            // Attempt to stringify anything else (object/array/etc.)
            const json = JSON.stringify(errorMsg)
            if (json && json !== '{}') return json
        } catch {
            // do nothing
        }
        return String(errorMsg)
    }
}

const httpWrapper = new HttpWrapper()
export default httpWrapper
